import React, { useState, useEffect } from "react";
import { Button } from "primereact/button";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { FilterMatchMode } from "primereact/api";
import LoadingOverlay from "react-loading-overlay";
import { Triangle } from "react-loader-spinner";
import { InputText } from "primereact/inputtext";
import { handleGetRequest } from "../../services/GetTemplate";
import { useDispatch } from "react-redux";
import { Tag } from "primereact/tag";
import { Dialog } from "primereact/dialog";
import { baseURL } from "../../Config";

const ManageJobApplication = () => {

    const dispatch = useDispatch()

    const [isActive, setIsActive] = useState(false)
    const [allApplications, setAllApplications] = useState([]);
    const [selectedApplication, setSelectedApplication] = useState(null);
    const [applicationDetailDialog, setApplicationDetailDialog] = useState(false);

    const [globalFilterValue, setGlobalFilterValue] = useState("");
    const [filters, setFilters] = useState({
        global: { value: null, matchMode: FilterMatchMode.CONTAINS },
    });

    const onGlobalFilterChange = (e) => {
        const value = e.target.value;
        let _filters = { ...filters };
        _filters["global"].value = value;

        setFilters(_filters);
        setGlobalFilterValue(value);
    };

    //Get Job Application List
    const getJobApplicationList = async () => {
        setIsActive(true);
        const response = await dispatch(handleGetRequest(`/job-applications/getAll`, true));
        if (response?.status === 'success' || response?.statusCode === 200) {
            setAllApplications(response?.data || []);
        }
        setIsActive(false);
    };

    useEffect(() => {
        getJobApplicationList();
    }, [])

    const dateTemplate = (rowData) => {
        if (rowData?.createdAt) {
            const date = new Date(rowData.createdAt);
            return date.toLocaleDateString('en-US', { 
                year: 'numeric', 
                month: 'short', 
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
            });
        }
        return 'N/A';
    };

    const nameTemplate = (rowData) => {
        return `${rowData?.firstName || ''} ${rowData?.lastName || ''}`.trim() || 'N/A';
    };

    const reviewedTemplate = (rowData) => {
        const isReviewed = rowData?.isReviewed || false;
        return (
            <Tag 
                value={isReviewed ? 'Reviewed' : 'Pending'} 
                severity={isReviewed ? 'success' : 'warning'} 
            />
        );
    };

    const resumeTemplate = (rowData) => {
        if (rowData?.resumeUrl || rowData?.resumeDownloadUrl) {
            const resumeUrl = rowData?.resumeDownloadUrl || `${baseURL}/${rowData.resumeUrl}`;
            return (
                <Button
                    label="Download"
                    icon="pi pi-download"
                    className="p-button-sm p-button-success"
                    onClick={(e) => {
                        e.stopPropagation();
                        window.open(resumeUrl, '_blank');
                    }}
                />
            );
        }
        return <span style={{ color: '#999' }}>No Resume</span>;
    };

    const actionTemplate = (rowData) => {
        return (
            <Button
                label="View Details"
                icon="pi pi-eye"
                className="p-button-sm"
                onClick={() => {
                    setSelectedApplication(rowData);
                    setApplicationDetailDialog(true);
                }}
            />
        );
    };

    const applicationDetailFooter = (
        <div>
            <Button
                label="Close"
                icon="pi pi-times"
                onClick={() => setApplicationDetailDialog(false)}
                className="p-button-text"
            />
        </div>
    );

    return (
        <>
            <div className="flex flex-column align-items-start md:flex-row  md:justify-content-between md:align-items-center ">
                <h4 className="mb-0" style={{ fontWeight: '600', letterSpacing: '0.5px', color: 'black' }}>
                    Job Applications Management
                </h4>
                <div className="flex flex-column align-items-start  md:flex-row md:align-items-center">
                    <span className="p-input-icon-left mr-3 resp-margin my-3 md:my-0">
                        <i className="pi pi-search" />
                        <InputText
                            placeholder="Search"
                            value={globalFilterValue}
                            onChange={onGlobalFilterChange}
                            className="search-input"
                        />
                    </span>
                </div>
            </div>

            <div className="card mt-3">
                <LoadingOverlay
                    active={isActive}
                    spinner={<Triangle
                        height="120"
                        width="120"
                        color="#dc2626"
                        ariaLabel="triangle-loading"
                        wrapperStyle={{}}
                        wrapperClassName=""
                        visible={true}
                    />}
                    styles={{
                        overlay: (base) => ({
                            ...base,
                            background: 'rgb(38 41 51 / 78%)',
                        })
                    }}
                >
                    <DataTable
                        responsive={true}
                        filters={filters}
                        globalFilterFields={[
                            "firstName",
                            "lastName",
                            "email",
                            "phone",
                            "position",
                            "experience",
                        ]}
                        responsiveLayout="scroll"
                        stripedRows
                        paginator
                        rows={20}
                        value={allApplications}
                        emptyMessage="No job applications found"
                        paginatorClassName="custom-paginator"
                        paginatorTemplate="CurrentPageReport FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"
                        currentPageReportTemplate="Showing {first} to {last} of {totalRecords}"
                        rowsPerPageOptions={[10, 20, 30, 40, 50]}
                    >
                        <Column sortable field='firstName' body={nameTemplate} header="Name"></Column>
                        <Column sortable field='email' body={(rowData) => rowData?.email || 'N/A'} header="Email"></Column>
                        <Column sortable field='phone' body={(rowData) => rowData?.phone || 'N/A'} header="Phone"></Column>
                        <Column sortable field='position' body={(rowData) => rowData?.position || 'N/A'} header="Position"></Column>
                        <Column field='experience' body={(rowData) => rowData?.experience || 'N/A'} header="Experience"></Column>
                        <Column field='resumeUrl' body={resumeTemplate} header="Resume"></Column>
                        {/* <Column field='isReviewed' body={reviewedTemplate} header="Status"></Column> */}
                        <Column field='createdAt' body={dateTemplate} header="Applied Date"></Column>
                        <Column header="Action" body={actionTemplate} />
                    </DataTable>
                </LoadingOverlay>
            </div>

            {/* Application Detail Dialog */}
            <Dialog
                header="Job Application Details"
                visible={applicationDetailDialog}
                style={{ width: '90vw', maxWidth: '800px' }}
                footer={applicationDetailFooter}
                onHide={() => setApplicationDetailDialog(false)}
                className="application-detail-dialog"
            >
                {selectedApplication && (
                    <div className="application-detail-content">
                        {/* Applicant Information Section */}
                        <div className="mb-4">
                            <h5 className="mb-3" style={{ color: '#dc2626', borderBottom: '2px solid #dc2626', paddingBottom: '8px' }}>
                                Applicant Information
                            </h5>
                            <div className="grid">
                                <div className="col-12 md:col-6">
                                    <div className="info-item mb-3">
                                        <label>Full Name:</label>
                                        <span>{`${selectedApplication.firstName || ''} ${selectedApplication.lastName || ''}`.trim() || 'N/A'}</span>
                                    </div>
                                    <div className="info-item mb-3">
                                        <label>Email:</label>
                                        <span>{selectedApplication.email || 'N/A'}</span>
                                    </div>
                                    <div className="info-item mb-3">
                                        <label>Phone:</label>
                                        <span>{selectedApplication.phone || 'N/A'}</span>
                                    </div>
                                </div>
                                <div className="col-12 md:col-6">
                                    <div className="info-item mb-3">
                                        <label>Position Applied:</label>
                                        <span>{selectedApplication.position || 'N/A'}</span>
                                    </div>
                                    <div className="info-item mb-3">
                                        <label>Experience:</label>
                                        <span>{selectedApplication.experience || 'N/A'}</span>
                                    </div>
                                    {/* <div className="info-item mb-3">
                                        <label>Status:</label>
                                        <span>{reviewedTemplate(selectedApplication)}</span>
                                    </div> */}
                                    <div className="info-item mb-3">
                                        <label>Applied Date:</label>
                                        <span>{new Date(selectedApplication.createdAt).toLocaleString()}</span>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Resume Section */}
                        {(selectedApplication.resumeUrl || selectedApplication.resumeDownloadUrl) && (
                            <div className="mb-4">
                                <h5 className="mb-3" style={{ color: '#dc2626', borderBottom: '2px solid #dc2626', paddingBottom: '8px' }}>
                                    Resume
                                </h5>
                                <div>
                                    <Button
                                        label="Download Resume"
                                        icon="pi pi-download"
                                        className="p-button-success"
                                        onClick={() => {
                                            const resumeUrl = selectedApplication.resumeDownloadUrl || `${baseURL}/${selectedApplication.resumeUrl}`;
                                            window.open(resumeUrl, '_blank');
                                        }}
                                    />
                                </div>
                            </div>
                        )}

                        {/* Cover Letter Section */}
                        {selectedApplication.coverLetter && (
                            <div className="mb-4">
                                <h5 className="mb-3" style={{ color: '#dc2626', borderBottom: '2px solid #dc2626', paddingBottom: '8px' }}>
                                    Cover Letter
                                </h5>
                                <div style={{ 
                                    padding: '15px', 
                                    backgroundColor: '#f8f9fa', 
                                    borderRadius: '8px',
                                    whiteSpace: 'pre-wrap',
                                    maxHeight: '400px',
                                    overflowY: 'auto'
                                }}>
                                    {selectedApplication.coverLetter}
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </Dialog>

            <style>{`
                .application-detail-content {
                    padding: 10px 0;
                }
                .info-item {
                    display: flex;
                    flex-direction: column;
                    gap: 4px;
                }
                .info-item label {
                    font-weight: 600;
                    color: #333;
                    font-size: 13px;
                    margin-bottom: 4px;
                }
                .info-item span {
                    color: #666;
                    font-size: 14px;
                }
                .application-detail-dialog .p-dialog-content {
                    padding: 1.5rem;
                }
            `}</style>
        </>
    );
};

export default ManageJobApplication;

